import L from "leaflet";
import { MapObject } from "../../ui/map-view";
import { JobType, ConnectProto, RouteStatus, TruckType, JobStatus } from "@racoon/api";
import { getTruckIconHTML, TRUCK_ICON_COMPONENTS } from "./truck-icons";
import { IcMapLocationPin } from "@racoon/icons";
import { renderToStaticMarkup } from "react-dom/server";
import { STATUS_TONE_HEX } from "@racoon/lib";
import { BoundedCache } from "../bounded-cache";
import { locationTypeStyle } from "./location-types";
import { countBadgeHtml } from "./count-badge";
import { jobStatusPinStroke, jobStatusTextClass, jobStatusTone } from "./job-status-colors";
import React from "react";

type RouteLike = {
  status?: RouteStatus | string;
  current_step_idx?: number | null;
  jobs?: Array<{ step_idx?: number | null; type?: JobType | string; status?: string | null }>;
};

const getCurrentJob = (route?: RouteLike) => {
  if (!route?.jobs?.length) return undefined;

  // Prefer current_step_idx (1-based) if present:
  const idx = (route.current_step_idx ?? 0) - 1;
  if (idx >= 0 && route.jobs[idx]) return route.jobs[idx];

  // Fallback: last job with non-cancelled status, else last:
  for (let i = route.jobs.length - 1; i >= 0; i--) {
    const job = route.jobs[i];
    if (job?.status && !`${job.status}`.endsWith("CANCELLED")) {
      return job;
    }
  }

  return route.jobs[route.jobs.length - 1];
};

/** renderToStaticMarkup is a full React render; the markup only varies by
 *  shape and colour, so it is rendered once per combination. */
const pinHtmlCache = new BoundedCache<string>(64);
// palette variants x badge label, and the label is a stop index.
const objectIconCache = new BoundedCache<L.DivIcon>(512);

export type PickupPalette = {
  /** Theme class when the status has a tone; the caller paints with currentColor. */
  colorClass?: string;
  fill: string;
  font: string;
};

type IconObject = MapObject | ConnectProto.PickupLocation;

/** An MSW site: one of its container groups' contracts prices JOB_TYPE_MSW. */
export const isHouseholdObject = (obj: IconObject): boolean =>
  "containerGroups" in obj &&
  (obj.containerGroups ?? []).some((cg) => {
    const contract = (cg as { contract?: { tariffRateByJobType?: Record<string, unknown> } })
      ?.contract;
    return Object.keys(contract?.tariffRateByJobType ?? {}).some((k) => k === "JOB_TYPE_MSW");
  });

/** Mirrors getPinIcon's branches, so a geofence drawn instead of a pin matches it. */
export const pickupPalette = (
  obj: IconObject,
  selected: boolean,
  jobStatus?: ConnectProto.JobStatus,
): PickupPalette => {
  if (obj.type.toString() === "depot") {
    return { fill: selected ? "#c22e26ff" : "#FF3B30", font: "#FFFFFF" };
  }
  const tone = jobStatus != null ? jobStatusTone(jobStatus) : undefined;
  if (tone?.bg) {
    const colorClass = jobStatusTextClass(jobStatus!);
    if (colorClass) return { colorClass, fill: "currentColor", font: "#FFFFFF" };
  }
  if ("hasUnassignedOrders" in obj && obj.hasUnassignedOrders && !obj.hasOrders) {
    return { fill: STATUS_TONE_HEX.pending.fill, font: STATUS_TONE_HEX.pending.text };
  }
  if (isHouseholdObject(obj)) {
    return { fill: selected ? "#1B8A38" : "#34C759", font: "#FFFFFF" };
  }
  return { fill: selected ? "#000000" : "#007AFF", font: "#FFFFFF" };
};

/** What the pin prints on itself; empty when the caller supplied no sequence number. */
export const pinBadgeLabel = (obj: IconObject): string =>
  "localIndex" in obj ? (obj.pinLabel ?? String(obj.localIndex ?? "")) : "";

const FAN_STEP_PX = 22;
const FAN_LIFT_PX = 18;

/** Horizontal distance from a fanned pin to its site; 0 outside a fan. */
export const fanOffsetPx = (obj: IconObject): number =>
  "fan" in obj && obj.fan && obj.fan.count > 1
    ? (obj.fan.index - (obj.fan.count - 1) / 2) * FAN_STEP_PX
    : 0;

/** Clears the whole row of a fan, which sits higher than a lone pin. */
export const pinTooltipOffset = (obj: MapObject): [number, number] =>
  obj.fan && obj.fan.count > 1 ? [0, -(28 + FAN_LIFT_PX + 4)] : [0, -28];

/** Line from a fanned pin's tip down to the site it stands for, plus a dot on the site. */
const fanLeader = (siteX: number, siteY: number) =>
  `<svg width="32" height="32" style="position:absolute;left:0;top:0;overflow:visible;z-index:-1;pointer-events:none">` +
  `<line x1="16" y1="24" x2="${siteX}" y2="${siteY}" stroke="#fff" stroke-width="3" stroke-linecap="round"/>` +
  `<line x1="16" y1="24" x2="${siteX}" y2="${siteY}" stroke="#475569" stroke-width="1.5" stroke-linecap="round"/>` +
  `<circle cx="${siteX}" cy="${siteY}" r="3.5" fill="#475569" stroke="#fff" stroke-width="1.5"/></svg>`;

const cardPinHtml = renderToStaticMarkup(
  React.createElement(IcMapLocationPin, { width: "27", height: "32" }),
).replace(/fill="#007AFF"/gi, 'fill="currentColor"');

/** The site pin as the hover card draws it, coloured like the pin on the map. */
export const siteCardPinHtml = (palette: PickupPalette): string =>
  `<span class="${palette.colorClass ?? ""}" style="display:flex;` +
  `${palette.colorClass ? "" : `color:${palette.fill}`}">${cardPinHtml}</span>`;

export const makeIcon = (
  obj: MapObject | ConnectProto.PickupLocation,
  selected: boolean,
  /** Unused: hover is a Leaflet Tooltip, not an icon swap. */
  _isHovered = false,
  number?: number,
  jobStatus?: ConnectProto.JobStatus,
  /** Off where the sequence number is array position rather than a stop order. */
  showIndex = true,
) => {
  const isHousehold = isHouseholdObject(obj);
  const isDepot = obj.type.toString() === "depot";
  const locationType = isDepot && "locationType" in obj ? obj.locationType : undefined;
  const palette = pickupPalette(obj, selected, jobStatus);
  let font_color = "#FFFFFF";
  let fill_class = "";
  let font_class = "";
  let stroke = "";

  const getPinIcon = (): string => {
    // Seeded from the palette so the depot branch keeps its red; the cases below
    // reassign the same values the palette already derived.
    let color = palette.fill;
    if (!isDepot) {
      // Job status-based coloring (if jobStatus is provided).
      // != null: 0 (UNDEFINED) is a valid enum value and must not be dropped.
      const tone = jobStatus != null ? jobStatusTone(jobStatus) : undefined;
      if (tone?.bg) {
        fill_class = jobStatusTextClass(jobStatus!) ?? "";
        font_class = tone.color;
        stroke = jobStatusPinStroke(jobStatus!) ?? "";
      } else if ("hasUnassignedOrders" in obj && obj.hasUnassignedOrders && !obj.hasOrders) {
        color = STATUS_TONE_HEX.pending.fill;
        font_color = STATUS_TONE_HEX.pending.text;
      } else if (isHousehold) {
        color = selected ? "#1B8A38" : "#34C759";
      } else {
        color = selected ? "#000000" : "#007AFF";
      }
    }

    // fill_class is part of the key: the markup differs when the pin paints
    // with a theme class instead of a literal colour.
    const htmlKey = `${isDepot}|${locationType}|${color}|${fill_class}|${stroke}`;
    const cachedHtml = pinHtmlCache.get(htmlKey);
    if (cachedHtml) return cachedHtml;

    const render = (): string => {
      if (isDepot) {
        const { Icon } = locationTypeStyle(locationType);
        const html = renderToStaticMarkup(React.createElement(Icon, { width: 28, height: 28 }));
        // A white rim keeps the flat Figma silhouettes legible over any tile.
        return (
          `<span style="display:flex;filter:drop-shadow(0 0 1px #fff) drop-shadow(0 0 1px #fff) ` +
          `drop-shadow(0 1px 2px rgba(0,0,0,.35))">${html.replace(/fill="currentColor"/gi, `fill="${color}"`)}</span>`
        );
      }
      const html = renderToStaticMarkup(
        React.createElement(IcMapLocationPin, {
          width: "21",
          height: "25",
        }),
      );
      if (fill_class) {
        return `<span class="${fill_class}" style="display: contents;">${html.replace(
          /fill="#007AFF"/gi,
          stroke
            ? `fill="currentColor" stroke="${stroke}" stroke-width="1"`
            : 'fill="currentColor"',
        )}</span>`;
      }
      return html.replace(/fill="#007AFF"/gi, `fill="${color}"`);
    };

    const html = render();
    pinHtmlCache.set(htmlKey, html);
    return html;
  };

  const badgeLabel = showIndex ? pinBadgeLabel(obj) : "";

  const pinIcon = getPinIcon();

  // Keyed on the palette, not the rendered SVG: the markup is multi-KB.
  const fan = "fan" in obj && obj.fan && obj.fan.count > 1 ? obj.fan : undefined;
  const nudge = isDepot && "nudge" in obj ? obj.nudge : undefined;
  // Where the pin sits relative to its site, in screen pixels.
  const [dx, dy] = nudge ?? [fanOffsetPx(obj), fan ? -FAN_LIFT_PX : 0];
  // A folded pin prints its lowest step; the badge says how many more it holds.
  const extraSteps = "stepJobs" in obj ? Math.max(0, (obj.stepJobs?.length ?? 1) - 1) : 0;
  const iconKey = `${extraSteps}|${isDepot}|${locationType}|${palette.colorClass ?? palette.fill}|${font_color}|${font_class}|${badgeLabel}|${dx}|${dy}`;
  const cachedIcon = objectIconCache.get(iconKey);
  if (cachedIcon) return cachedIcon;

  // A label wider than the pin head goes in a pill above the pin instead.
  const fitsHead = badgeLabel.length <= 2;
  const pill =
    isDepot || !badgeLabel || fitsHead
      ? ""
      : `<div class="${palette.colorClass ?? ""}" style="position:absolute;bottom:26px;left:50%;` +
        `transform:translateX(-50%);z-index:1;white-space:nowrap;border-radius:8px;` +
        `border:1px solid #fff;padding:1px 6px;font-size:12px;line-height:15px;font-weight:700;` +
        `background:${palette.colorClass ? "currentColor" : palette.fill};pointer-events:none">` +
        `<span class="${font_class}" style="${font_class ? "" : `color:${font_color}`}">${badgeLabel}</span></div>`;

  // Drawn into the pin's own SVG rather than overlaid: an absolutely positioned
  // span is at the mercy of whatever stacking context the host map sets up.
  const pinWithLabel =
    isDepot || !badgeLabel || !fitsHead
      ? pinIcon
      : pinIcon.replace(
          /<\/svg>(?![\s\S]*<\/svg>)/,
          `<text x="10.6" y="10.2" text-anchor="middle" dominant-baseline="central" ` +
            `font-family="Inter, system-ui, sans-serif" font-size="11" font-weight="700" ` +
            (font_class ? `class="${font_class}" fill="currentColor"` : `fill="${font_color}"`) +
            ` style="pointer-events:none">${badgeLabel}</text></svg>`,
        );

  const icon = L.divIcon({
    className: "object-marker",
    html: `
        <div style="
          position: relative; width: 32px; height: 32px;
          display: flex; justify-content: center;
        ">
          ${fan || nudge ? fanLeader(16 - dx, 32 - dy) : ""}${pinWithLabel}${pill}${extraSteps > 0 ? countBadgeHtml(`+${extraSteps}`, "top:-6px;left:20px") : ""}
        </div>
      `,
    iconSize: [32, 32],
    // The site sits below the row of pins, so each icon anchors off its own centre.
    iconAnchor: [16 - dx, 32 - dy],
    popupAnchor: [0, -32],
  });

  objectIconCache.set(iconKey, icon);
  return icon;
};

type ResolveParams = {
  routeStatus?: RouteStatus | string;
  jobType?: JobType | string;
  jobStatus?: JobStatus | string;
  selected?: boolean;
  trucktype?: string;
};

/** The dark ring the drawn *Selected sprites have, traced around any sprite. */
const SELECTED_OUTLINE = [
  [1.5, 0],
  [-1.5, 0],
  [0, 1.5],
  [0, -1.5],
]
  .map(([x, y]) => `drop-shadow(${x}px ${y}px 0 #1D1F23)`)
  .join(" ");

const resolveTruckSprite = ({
  routeStatus,
  jobType,
  jobStatus,
  selected,
  trucktype,
}: ResolveParams): string => {
  const sfx = selected ? "Selected" : "";

  // Normalize to strings to be resilient to enum objects
  const r = `${routeStatus ?? ""}`;
  const j = `${jobType ?? ""}`;
  const s = `${jobStatus ?? ""}`;
  const t = `${trucktype ?? ""}`;
  // Resolve component name based on route status and job type
  // Default to bunker icon for construction trucks
  let componentName: keyof typeof TRUCK_ICON_COMPONENTS =
    `icTruckEnrouteBunker${sfx as "" | "Selected"}` as keyof typeof TRUCK_ICON_COMPONENTS;

  // 1) Returning → Full
  if (r === "ROUTE_STATUS_RETURNING") {
    if (t === TruckType.TRUCK_TYPE_CONSTRUCTION)
      componentName =
        `icTruckEnrouteFull${sfx as "" | "Selected"}` as keyof typeof TRUCK_ICON_COMPONENTS;
    else if (t === TruckType.TRUCK_TYPE_DOUBLE_CONTAINER)
      componentName =
        `icTruckEnrouteSamosvalFull${sfx as "" | "Selected"}` as keyof typeof TRUCK_ICON_COMPONENTS;
  }
  // 2) In progress + (Replace Bunker | Add Bunker) + On Service → Service
  else if (
    r === "ROUTE_STATUS_IN_PROGRESS" &&
    (j === "JOB_TYPE_REPLACE_BUNKER" || j === "JOB_TYPE_ADD_BUNKER") &&
    s === "JOB_STATUS_ON_SERVICE"
  ) {
    if (t === TruckType.TRUCK_TYPE_CONSTRUCTION)
      componentName =
        `icTruckService${sfx as "" | "Selected"}` as keyof typeof TRUCK_ICON_COMPONENTS;
    else if (t === TruckType.TRUCK_TYPE_DOUBLE_CONTAINER)
      componentName =
        `icTruckSamosvalService${sfx as "" | "Selected"}` as keyof typeof TRUCK_ICON_COMPONENTS;
  }
  // 3) Remove bunker → Free
  else if (j === "JOB_TYPE_REMOVE_BUNKER") {
    if (t === TruckType.TRUCK_TYPE_CONSTRUCTION)
      componentName =
        `icTruckEnrouteFree${sfx as "" | "Selected"}` as keyof typeof TRUCK_ICON_COMPONENTS;
  }
  // 4) Default → Bunker (already initialized above)
  else {
    if (t === TruckType.TRUCK_TYPE_CONSTRUCTION)
      componentName =
        `icTruckEnrouteBunker${sfx as "" | "Selected"}` as keyof typeof TRUCK_ICON_COMPONENTS;
    else if (t === TruckType.TRUCK_TYPE_DOUBLE_CONTAINER)
      componentName =
        `icTruckEnrouteSamosval${sfx as "" | "Selected"}` as keyof typeof TRUCK_ICON_COMPONENTS;
  }
  if (t === TruckType.TRUCK_TYPE_MSW) {
    componentName =
      `icTruckEnrouteMsw${sfx as "" | "Selected"}` as keyof typeof TRUCK_ICON_COMPONENTS;
  }
  if (t === TruckType.TRUCK_TYPE_LIQUID_WASTE) {
    componentName = `icTruckLiquid${sfx as "" | "Selected"}` as keyof typeof TRUCK_ICON_COMPONENTS;
  }
  if (t === TruckType.TRUCK_TYPE_TIPPER || t === TruckType.TRUCK_TYPE_LOADER_CRANE) {
    const base = t === TruckType.TRUCK_TYPE_TIPPER ? "icTruckTipper" : "icTruckLoaderCrane";
    const state =
      r === "ROUTE_STATUS_RETURNING" ? "Full" : s === "JOB_STATUS_ON_SERVICE" ? "Service" : "";
    const html = getTruckIconHTML(`${base}${state}` as keyof typeof TRUCK_ICON_COMPONENTS);
    // No drawn Selected variant: outline the silhouette instead.
    return selected ? html.replace("<svg", `<svg style="filter:${SELECTED_OUTLINE}"`) : html;
  }
  // Get rendered HTML from the truck icon component
  return getTruckIconHTML(componentName);
};

// Convenience: derive from full MapObject
const getTruckSpriteForObject = (obj: MapObject, selected = false): string => {
  const route = (obj as any)?.route as RouteLike | undefined;
  const trucktype = (obj as any)?.truckType as string | undefined;
  const routeStatus = route?.status;

  const currentJob = getCurrentJob(route);
  const jobType = (currentJob?.type as JobType) ?? "UNRECOGNIZED";
  const jobStatus = (currentJob?.status as string) ?? undefined;

  return resolveTruckSprite({ routeStatus, jobType, jobStatus, selected, trucktype });
};

/** Inputs that decide which sprite is picked, hence its colour. */
const truckSpriteKey = (obj: MapObject, selected: boolean): string => {
  const route = (obj as any)?.route as RouteLike | undefined;
  const job = getCurrentJob(route);
  return [route?.status, job?.type, job?.status, (obj as any)?.truckType, selected].join("|");
};

const spriteColorCache = new BoundedCache<string>(256);

/** Most common saturated fill in the sprite, so the chevron reads as the same
 *  vehicle. Greys and near-black/white are chrome, not identity. */
const dominantSpriteColor = (html: string): string => {
  const tally = new Map<string, number>();
  for (const m of html.matchAll(/fill="(#[0-9a-fA-F]{6})"/g)) {
    const hex = m[1].toUpperCase();
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    if (max - min < 30 || max < 40 || min > 225) continue;
    tally.set(hex, (tally.get(hex) ?? 0) + 1);
  }
  let best = "#007AFF";
  let bestCount = 0;
  for (const [hex, count] of tally) {
    if (count > bestCount) {
      best = hex;
      bestCount = count;
    }
  }
  return best;
};

const truckChevronCache = new BoundedCache<L.DivIcon>(256);

/** Low-zoom stand-in: position, state colour and heading, at a twelfth of the
 *  sprite's area. The sprite's own detail is unreadable at that scale. */
export const makeTruckChevronIcon = (obj: MapObject, selected = false): L.DivIcon => {
  const spriteKey = truckSpriteKey(obj, selected);
  let color = spriteColorCache.get(spriteKey);
  if (!color) {
    color = dominantSpriteColor(getTruckSpriteForObject(obj, selected));
    spriteColorCache.set(spriteKey, color);
  }

  const angle = Math.round((((obj as any)?.angle ?? 0) % 360) / 5) * 5;
  const size = selected ? 26 : 20;
  const cacheKey = `${color}|${angle}|${size}`;
  const cached = truckChevronCache.get(cacheKey);
  if (cached) return cached;

  const icon = L.divIcon({
    className: "truck-chevron",
    html:
      `<svg width="${size}" height="${size}" viewBox="0 0 16 16" ` +
      `style="transform:rotate(${angle}deg);display:block;overflow:visible">` +
      `<path d="M8 1 L14 15 L8 11.4 L2 15 Z" fill="${color}" stroke="#fff" ` +
      `stroke-width="1.4" stroke-linejoin="round" ` +
      `style="filter:drop-shadow(0 1px 1px rgba(0,0,0,.35))"/></svg>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
  truckChevronCache.set(cacheKey, icon);
  return icon;
};

// fleet x angle bucket: the one key space that genuinely reaches its cap.
const truckIconCache = new BoundedCache<L.DivIcon>(2000, true);

export const makeTruckIconDynamic = (obj: MapObject, selected = false, showLabel = true) => {
  const route = (obj as any)?.route as RouteLike | undefined;
  const currentJob = getCurrentJob(route);
  const label = showLabel ? ((obj as any)?.name ?? "") : "";
  // Rounded so a jittering heading does not rebuild the marker element, which
  // is what setIcon does on every new icon identity.
  const angle = Math.round((((obj as any)?.angle ?? 0) % 360) / 5) * 5;

  const cacheKey = [
    route?.status,
    currentJob?.type,
    currentJob?.status,
    (obj as any)?.truckType,
    selected,
    angle,
    label,
  ].join("|");
  const cached = truckIconCache.get(cacheKey);
  if (cached) return cached;

  const sprite = getTruckSpriteForObject(obj, selected);

  const icon = L.divIcon({
    className: "truck-marker",
    html: `
        <div style="position: relative;">
          ${
            label
              ? `<div style="
            background-color:#303338F2; color:#fff;
            padding-left:8px; padding-right:6px;
            width: max-content; max-width: 160px;
            border-radius: 12px; font-size:12px; line-height: 22px;
            position: absolute; top: -30%; left: 50%; transform: translateX(-50%);
            white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
          ">${label}</div>`
              : ""
          }
          <div style="display:inline-block; transform: rotate(${angle}deg);">
            ${sprite}
          </div>
        </div>
      `,
    iconSize: [48, 52],
  });

  // Angle buckets times fleet size stays small, but never unbounded.
  truckIconCache.set(cacheKey, icon);
  return icon;
};
